import i18n from "@/i18n";
/**
 * WorkforceReviewHours — Prompt 13C.
 *
 * Manager Review Hours page. Reuses the canonical Period discovery
 * flow (`GET /workforce/periods/current` with a browser-local anchor
 * date), then loads Review Hours (`GET /workforce/review-hours`)
 * for that Period. Delegates all business rules to the 13B backend:
 * planned/actual/difference/reviewable/reviewed values are read
 * verbatim from the API response.
 *
 * Actual Entry CRUD and verification set/clear are performed
 * through the canonical 13B routes:
 *   POST   /workforce/actual-entries
 *   PATCH  /workforce/actual-entries/{id}
 *   DELETE /workforce/actual-entries/{id}
 *   PUT    /workforce/review-hours/review-status
 *
 * After every successful mutation, the page reloads canonical
 * Review Hours state — never optimistic.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import {
    browserLocalYMD, formatPeriodRange,
} from "@/components/workforce/workforceHelpers";
import ScheduleGrid from "@/components/workforce/ScheduleGrid";
import ActualCellEditor from "@/components/workforce/ActualCellEditor";
import AveragingCloseValidity from "@/components/workforce/AveragingCloseValidity";
import StandardCloseValidity from "@/components/workforce/StandardCloseValidity";
import AveragingSettlementPanel from "@/components/workforce/AveragingSettlementPanel";
import PeriodCloseControl from
    "@/components/workforce/PeriodCloseControl";
import ExportPayrollButton from
    "@/components/workforce/ExportPayrollButton";
import { useAbsenceActions } from "@/components/workforce/useAbsenceActions";
import { canManageAbsences } from "@/components/workforce/absenceShared";
import {
    rulesForEmployee, rulesForCell,
} from "@/components/workforce/workforceRules";

const _PERIOD_ANCHOR_CODE = "WORKFORCE_PERIOD_ANCHOR_REQUIRED";
const _GENERIC_MESSAGE = "Could not load review hours.";
const _EMPTY_MESSAGE = "No employees in this review period.";
const _MSG_OVERLAP =
    "This actual block overlaps another. Adjust the times.";
const _MSG_REVISION = "Data changed. Please try again.";
const _MSG_GENERIC = "Could not save. Try again.";
// Prompt 17B2 §16 · §6 — verbatim manager-facing copy for a canonical
// closed-period 409 during a Review Hours mutation.
const _MSG_PERIOD_CLOSED =
    "This period is closed. Reopen it before making changes.";
const _READONLY_BANNER_TEXT = "Period closed";
const _READONLY_BANNER_HINT = "Reopen the period to make changes.";

// Attach the existing per-day clock evidence sessions to each grid cell.
function withClockEvidence(rh) {
    const ev = (rh && rh.clock_evidence_by_employee) || {};
    return {
        ...rh,
        employees: (rh.employees || []).map((row) => ({
            ...row,
            cells: (row.cells || []).map((c) => {
                const sessions = (ev[row.employee_id] || {})[c.date] || [];
                return {
                    ...c,
                    clock_sessions: sessions,
                    clock_incomplete: sessions.some((s) => s.status !== "COMPLETE"),
                };
            }),
        })),
    };
}

function PlanSourceNotice({ plan }) {
    if (!plan) return null;
    if (plan.source === "UNPUBLISHED") {
        return (
            <div data-testid="workforce-review-plan-unpublished"
                className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                {i18n.t("wf.workforceReviewHours.thisScheduleIsNotPublishedApprove", "This schedule is not published. Approve matching only approves against a published schedule.")}
            </div>
        );
    }
    if (plan.source === "PUBLISHED" && plan.unpublished_changes) {
        return (
            <div data-testid="workforce-review-plan-unpublished-changes"
                className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                {i18n.t("wf.workforceReviewHours.theScheduleHasChangesThatAre", "The schedule has changes that are not published. Review Hours compares against the published schedule (version {{version}}).", { version: plan.publication_version })}
            </div>
        );
    }
    return null;
}

export default function WorkforceReviewHours() {
    const { restaurantId, user } = useApp();

    const [period, setPeriod] = useState(null);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errorKind, setErrorKind] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editorContext, setEditorContext] = useState(null);
    const [pending, setPending] = useState(false);
    // Prompt 17B2 §6 · §14 · §16 — current Close state hydrated by
    // PeriodCloseControl. `null` = unknown/loading. When CLOSED the
    // page becomes read-only.
    const [closeState, setCloseState] = useState(null);
    const periodClosed = closeState && closeState.current_state === "CLOSED";
    // CALENDAR-ABSENCES-V1 — manager-only absence add / edit / cancel from the calendar.
    const reloadRef = useRef(null);
    const absenceUi = useAbsenceActions({
        user, restaurantId, view: "review",
        employees: data?.employees, locked: Boolean(periodClosed),
        onChanged: () => reloadRef.current?.(),
    });
    // Prompt 18B2 §2 · §3 · §11 · §21 · §24 · §25 — bounded ACTIVE
    // absence map for the visible Review period. Keyed by
    // employee_id → [absence rows]. Fetched ONCE per period load
    // (never per row/date · §25). Persists in CLOSED periods so
    // historical absence context remains visible (§11).
    // Location / employee switch clears via clearAll (§24).
    const [absencesByEmployee, setAbsencesByEmployee] = useState({});
    // Prompt 18B2-P3 §23-§29 — bulk manager confirmation of GREEN
    // rows via the additive `/review-hours/approve-matching` endpoint.
    const [approving, setApproving] = useState(false);

    const reqIdRef = useRef(0);

    const clearAll = useCallback(() => {
        setPeriod(null);
        setData(null);
        setErrorKind(null);
        setEditorOpen(false);
        setEditorContext(null);
        setAbsencesByEmployee({});
    }, []);

    const loadForDate = useCallback(async (dateYmd) => {
        if (!restaurantId) return;
        const rid = restaurantId;
        const myReq = ++reqIdRef.current;
        setLoading(true);
        setErrorKind(null);
        setData(null);
        setPeriod(null);
        setAbsencesByEmployee({});
        try {
            const { data: periodData } = await api.get(
                "/workforce/periods/current",
                { params: { restaurant_id: rid, date: dateYmd } },
            );
            if (myReq !== reqIdRef.current) return;
            setPeriod(periodData);
            const { data: rhData } = await api.get(
                "/workforce/review-hours",
                {
                    params: {
                        restaurant_id: rid,
                        period_id: periodData.period_id,
                    },
                },
            );
            if (myReq !== reqIdRef.current) return;
            setData(withClockEvidence(rhData));
            // Prompt 18B2 §2 · §3 · §11 · §21 · §25 — bounded
            // ACTIVE absence fetch for the visible period. ONE
            // request per Review Hours load. Failures are silent
            // so Review Hours remains usable without absence
            // context (§26 · never invent absence data).
            try {
                const { data: absData } = await api.get(
                    "/workforce/absences",
                    { params: {
                        restaurant_id: rid,
                        start_date: periodData.start_date,
                        end_date: periodData.end_date,
                    } });
                if (myReq === reqIdRef.current) {
                    const map = {};
                    for (const a of (absData?.absences || [])) {
                        (map[a.employee_id] =
                            map[a.employee_id] || []).push(a);
                    }
                    setAbsencesByEmployee(map);
                }
            } catch {
                if (myReq === reqIdRef.current) {
                    setAbsencesByEmployee({});
                }
            }
        } catch (err) {
            if (myReq !== reqIdRef.current) return;
            const code = err && err.response && err.response.data
                && err.response.data.detail
                ? err.response.data.detail.code : undefined;
            setErrorKind(code === _PERIOD_ANCHOR_CODE ? "anchor" : "generic");
            setPeriod(null);
            setData(null);
            setAbsencesByEmployee({});
        } finally {
            if (myReq === reqIdRef.current) setLoading(false);
        }
    }, [restaurantId]);

    const approveMatching = useCallback(async () => {
        if (!restaurantId || !period || approving) return;
        setApproving(true);
        try {
            const { data: out } = await api.post(
                "/workforce/review-hours/approve-matching",
                null,
                { params: {
                    restaurant_id: restaurantId,
                    period_id: period.period_id,
                } });
            toast.success(
                `Approved ${out.approved_count} · `
                + `skipped ${out.skipped_count}`);
            await loadForDate(period.start_date);
        } catch (err) {
            toast.error(i18n.t("wf.workforceReviewHours.approveMatchingFailed", "Approve matching failed"));
        } finally {
            setApproving(false);
        }
    }, [restaurantId, period, approving, loadForDate]);

    useEffect(() => {
        clearAll();
        if (!restaurantId) return;
        loadForDate(browserLocalYMD());
    }, [restaurantId, clearAll, loadForDate]);

    const goPrevious = useCallback(() => {
        if (!period || loading) return;
        loadForDate(period.previous_start_date);
    }, [period, loading, loadForDate]);
    const goNext = useCallback(() => {
        if (!period || loading) return;
        loadForDate(period.next_start_date);
    }, [period, loading, loadForDate]);

    const reload = useCallback(() => {
        if (period) loadForDate(period.start_date);
        else loadForDate(browserLocalYMD());
    }, [period, loadForDate]);
    reloadRef.current = reload;

    // Cell click → open editor with the exact Actual blocks for this
    // employee/day. Backend response already carries the canonical
    // Actual list under `data.actual_entries`.
    const handleReviewCellClick = useCallback((employeeId, cell) => {
        if (!data) return;
        const blocks = (data.actual_entries || []).filter((a) => (
            a.employee_id === employeeId && a.work_date === cell.date));
        const emp = (data.employees || []).find(
            (e) => e.employee_id === employeeId);
        // Prompt 14D §12 — resolve per-cell rules payload using the
        // canonical source_actual_entry_id mapping.
        const actualIds = new Set(blocks.map((b) => b.actual_entry_id));
        const empRules = rulesForEmployee(data.rules, employeeId);
        const cellRules = rulesForCell(empRules, actualIds);
        // Prompt 15B §14 · §17 — clock evidence sessions for the
        // same employee/day. Structure is `{employee_id: {work_date: [...]}}`.
        const evidenceMap = (data.clock_evidence_by_employee || {});
        const empEvidence = (evidenceMap[employeeId] || {});
        const clockSessions = empEvidence[cell.date] || [];
        setEditorContext({
            employee_id: employeeId,
            employee_name: emp
                ? `${emp.first_name} ${emp.last_name}`.trim() : employeeId,
            work_date: cell.date,
            blocks,
            reviewed: Boolean(cell.reviewed),
            reviewable: Boolean(cell.reviewable),
            rules: cellRules,
            clock_evidence: clockSessions,
            // Prompt 18B2-P7B §6 · §12 · §21 — carry the canonical
            // per-cell Staff Meal fact into the editor so the
            // manager sees the current state before correcting.
            staff_meal: (cell.staff_meal === true
                || cell.staff_meal === false)
                ? cell.staff_meal
                : null,
        });
        setEditorOpen(true);
    }, [data]);

    const _errCode = (err) => (
        err && err.response && err.response.data
            && err.response.data.detail
            && (typeof err.response.data.detail === "object"
                ? err.response.data.detail.code
                : err.response.data.detail));

    const _showErr = (code) => {
        if (code === "WORKFORCE_ACTUAL_ENTRY_OVERLAP") {
            toast.error(i18n.t("wf.WorkforceReviewHours._MSG_OVERLAP", _MSG_OVERLAP));
        } else if (code === "WORKFORCE_REVISION_CONFLICT") {
            toast.error(i18n.t("wf.WorkforceReviewHours._MSG_REVISION", _MSG_REVISION));
        } else if (code === "WORKFORCE_PERIOD_CLOSED") {
            // Prompt 17B2 §16 — known conflict; manager-safe toast.
            toast.error(i18n.t("wf.WorkforceReviewHours._MSG_PERIOD_CLOSED", _MSG_PERIOD_CLOSED));
        } else {
            toast.error(i18n.t("wf.WorkforceReviewHours._MSG_GENERIC", _MSG_GENERIC));
        }
    };

    const doCreate = useCallback(async (payload) => {
        if (!editorContext || !period || pending) return;
        setPending(true);
        try {
            await api.post("/workforce/actual-entries", {
                restaurant_id: restaurantId,
                period_id: period.period_id,
                employee_id: editorContext.employee_id,
                ...payload,
            });
            await loadForDate(period.start_date);
            setEditorOpen(false);
        } catch (err) {
            _showErr(_errCode(err));
        } finally { setPending(false); }
    }, [editorContext, period, pending, restaurantId, loadForDate]);

    const doUpdate = useCallback(async (payload) => {
        if (!period || pending) return;
        setPending(true);
        try {
            const { actual_entry_id, ...rest } = payload;
            await api.patch(
                `/workforce/actual-entries/${actual_entry_id}`,
                { restaurant_id: restaurantId, ...rest },
            );
            await loadForDate(period.start_date);
            setEditorOpen(false);
        } catch (err) {
            _showErr(_errCode(err));
        } finally { setPending(false); }
    }, [period, pending, restaurantId, loadForDate]);

    const doDelete = useCallback(async ({
        actual_entry_id, expected_revision, ...correction
    }) => {
        if (!period || pending) return;
        setPending(true);
        try {
            await api.delete(
                `/workforce/actual-entries/${actual_entry_id}`,
                {
                    params: {
                        restaurant_id: restaurantId,
                        expected_revision,
                        ...correction,
                    },
                },
            );
            await loadForDate(period.start_date);
            setEditorOpen(false);
        } catch (err) {
            _showErr(_errCode(err));
        } finally { setPending(false); }
    }, [period, pending, restaurantId, loadForDate]);

    const setReviewed = useCallback(async (reviewedFlag) => {
        if (!editorContext || !period || pending) return;
        setPending(true);
        try {
            await api.put("/workforce/review-hours/review-status", {
                restaurant_id: restaurantId,
                period_id: period.period_id,
                employee_id: editorContext.employee_id,
                work_date: editorContext.work_date,
                reviewed: reviewedFlag,
            });
            await loadForDate(period.start_date);
            setEditorOpen(false);
        } catch (err) {
            _showErr(_errCode(err));
        } finally { setPending(false); }
    }, [editorContext, period, pending, restaurantId, loadForDate]);

    // Prompt 18B2-P7B §6 · §7 · §12 — manager Staff Meal correction.
    // Persists the canonical three-way value (true / false / null)
    // through the additive `PUT /review-hours/staff-meal` endpoint
    // and reloads Review Hours so time-derived status recomputes
    // automatically (§12).
    const saveStaffMeal = useCallback(async (nextValue) => {
        if (!editorContext || !period || pending) return;
        setPending(true);
        try {
            await api.put("/workforce/review-hours/staff-meal", {
                restaurant_id: restaurantId,
                period_id: period.period_id,
                employee_id: editorContext.employee_id,
                local_date: editorContext.work_date,
                staff_meal: nextValue,
            });
            await loadForDate(period.start_date);
        } catch (err) {
            _showErr(_errCode(err));
        } finally { setPending(false); }
    }, [editorContext, period, pending, restaurantId, loadForDate]);

    // Prompt 16A §19 — additive READ-ONLY Averaging indicator.
    // Rendered only when at least one employee row carries
    // `working_time_model === "AVERAGING"`. This indicator MUST NOT
    // alter Actual editing, Verification, Clock Evidence use, or the
    // Rules Engine payload — it is purely a visibility hint.
    const averagingEmployeeIds = useMemo(() => {
        if (!data || !Array.isArray(data.employees)) return [];
        return data.employees
            .filter((e) => e && e.working_time_model === "AVERAGING")
            .map((e) => e.employee_id);
    }, [data]);

    const rangeText = useMemo(() => {
        if (!period) return "";
        return formatPeriodRange(period.start_date, period.end_date);
    }, [period]);

    // Aggregate review-progress across all rows for the compact
    // indicator (§11).
    const progress = useMemo(() => {
        if (!data || !Array.isArray(data.employees)) return null;
        let reviewable = 0;
        let reviewed = 0;
        for (const row of data.employees) {
            reviewable += Number(row.reviewable_days || 0);
            reviewed += Number(row.reviewed_days || 0);
        }
        return { reviewable, reviewed };
    }, [data]);

    const showLoading = loading;
    const showAnchor = !loading && errorKind === "anchor";
    const showError = !loading && errorKind === "generic";
    const showEmpty = !loading && !errorKind && data
        && Array.isArray(data.employees) && data.employees.length === 0;
    const showGrid = !loading && !errorKind && data
        && Array.isArray(data.employees) && data.employees.length > 0;

    return (
        <div data-testid="workforce-review-hours-page">
            <PageHeader
                title={i18n.t("wf.workforceReviewHours.reviewHours", "Review Hours")}
                testId="workforce-review-hours-header"
                action={
                    <span className="inline-flex items-center gap-2">
                        <PeriodCloseControl
                            restaurantId={restaurantId}
                            periodId={period ? period.period_id : null}
                            user={user}
                            onStateChanged={setCloseState}
                        />
                        <ExportPayrollButton
                            restaurantId={restaurantId}
                            periodId={period ? period.period_id : null}
                            closeState={closeState}
                            user={user}
                        />
                    </span>
                }
            />
            <div
                data-testid="workforce-review-hours-content"
                className="px-4 sm:px-6 py-6 space-y-4"
            >
                <AveragingCloseValidity closeState={closeState} />
                <StandardCloseValidity closeState={closeState} />
                <AveragingSettlementPanel closeState={closeState} user={user} restaurantId={restaurantId} />
                <div
                    data-testid="workforce-review-hours-nav"
                    className="flex items-center gap-3"
                >
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goPrevious}
                        disabled={!period || loading}
                        data-testid="workforce-review-hours-nav-previous"
                    >
                        <ChevronLeft className="h-4 w-4" strokeWidth={2} />
                        {i18n.t("wf.workforceReviewHours.previous", "Previous")}
                    </Button>
                    <span
                        data-testid="workforce-review-hours-nav-range"
                        className="text-sm text-neutral-700"
                    >
                        {rangeText}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={goNext}
                        disabled={!period || loading}
                        data-testid="workforce-review-hours-nav-next"
                    >
                        {i18n.t("common.next", "Next")}
                        <ChevronRight className="h-4 w-4" strokeWidth={2} />
                    </Button>
                    {progress && (
                        <span
                            data-testid="workforce-review-hours-progress"
                            className="ml-auto text-sm text-neutral-500"
                        >
                            {i18n.t("wf.workforceReviewHours.progress", "{{reviewed}} of {{total}} reviewed", { reviewed: progress.reviewed, total: progress.reviewable })}
                        </span>
                    )}
                </div>

                {averagingEmployeeIds.length > 0 && (
                    <div
                        data-testid="workforce-review-hours-averaging-indicator"
                        className="inline-flex items-center gap-2 rounded-full
                                    border border-neutral-200 bg-neutral-50
                                    px-3 py-1 text-xs text-neutral-700"
                    >
                        {i18n.t("wf.workforceReviewHours.averaging", "Averaging")}
                        <span
                            data-testid={
                                "workforce-review-hours-averaging-count"}
                            className="rounded-full bg-neutral-200 px-2
                                        text-[10px]"
                        >
                            {averagingEmployeeIds.length}
                        </span>
                    </div>
                )}

                {/* Prompt 18B2-P7B §17 — lightweight period-level
                    Staff Meal summary. Never shows monetary values. Hidden when the current period has no
                    Staff Meal activity so it never crowds the
                    exception-first layout. */}
                {data && data.staff_meal_summary
                    && (data.staff_meal_summary.count_yes > 0
                        || data.staff_meal_summary.count_missing > 0) && (
                    <div
                        data-testid="workforce-review-hours-staff-meal-summary"
                        className="inline-flex items-center gap-3 rounded-md
                                    border border-neutral-200 bg-white px-3
                                    py-1 text-xs text-neutral-700"
                    >
                        <span
                            data-testid={
                                "workforce-review-hours-staff-meal-count-yes"}
                        >
                            {i18n.t("wf.workforceReviewHours.staffMealsCount", "Staff meals: {{count}}", { count: data.staff_meal_summary.count_yes })}
                        </span>
                        {data.staff_meal_summary.count_missing > 0 && (
                            <span
                                data-testid="workforce-review-hours-staff-meal-count-missing"
                                className="text-amber-800"
                            >
                                {i18n.t("wf.workforceReviewHours.mealAnswersMissing", "Meal answers missing: {{count}}", { count: data.staff_meal_summary.count_missing })}
                            </span>
                        )}
                    </div>
                )}

                {showLoading && (
                    <div
                        data-testid="workforce-review-hours-loading"
                        className="space-y-2"
                    >
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton
                                key={i}
                                className="h-10 w-full"
                            />
                        ))}
                    </div>
                )}

                {showAnchor && (
                    <div
                        data-testid="workforce-review-hours-anchor-required"
                        className="rounded-md border border-neutral-200
                                    bg-white p-8 text-center space-y-3"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.workforceReviewHours.setUpTheScheduleForThis", "Set up the schedule for this location before reviewing hours.")}
                        </p>
                    </div>
                )}

                {showError && (
                    <div
                        data-testid="workforce-review-hours-error"
                        className="rounded-md border border-neutral-200
                                    bg-white p-6 text-center space-y-3"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.WorkforceReviewHours._GENERIC_MESSAGE", _GENERIC_MESSAGE)}
                        </p>
                        <Button
                            variant="outline"
                            onClick={reload}
                            data-testid="workforce-review-hours-retry"
                        >
                            {i18n.t("wf.workforceReviewHours.retry", "Retry")}
                        </Button>
                    </div>
                )}

                {showEmpty && (
                    <div
                        data-testid="workforce-review-hours-empty"
                        className="rounded-md border border-neutral-200
                                    bg-white p-8 text-center"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.WorkforceReviewHours._EMPTY_MESSAGE", _EMPTY_MESSAGE)}
                        </p>
                    </div>
                )}

                {showGrid && (
                    <>
                        {periodClosed && (
                            <div
                                data-testid="workforce-review-hours-closed-banner"
                                className="rounded-md border border-neutral-300
                                            bg-neutral-50 px-4 py-2 text-sm
                                            text-neutral-700"
                            >
                                <span className="font-medium">
                                    {i18n.t("wf.WorkforceReviewHours._READONLY_BANNER_TEXT", _READONLY_BANNER_TEXT)}
                                </span>
                                <span className="ml-1 text-neutral-500">
                                    {i18n.t("wf.WorkforceReviewHours._READONLY_BANNER_HINT", _READONLY_BANNER_HINT)}
                                </span>
                            </div>
                        )}
                        {!periodClosed && <PlanSourceNotice plan={data.plan} />}
                        <div className="flex justify-end gap-2">
                            {absenceUi.toolbar}
                        {!periodClosed && (
                                <Button
                                    data-testid="workforce-review-approve-matching"
                                    onClick={approveMatching}
                                    disabled={approving}
                                    variant="outline"
                                >
                                    {approving ? i18n.t("wf.workforceReviewHours.approving", "Approving…")
                                        : i18n.t("wf.workforceReviewHours.approveMatchingHours", "Approve matching hours")}
                                </Button>
                        )}
                        </div>
                        <ScheduleGrid
                            mode="review"
                            dates={data.dates}
                            employees={data.employees}
                            onReviewCellClick={handleReviewCellClick}
                            rules={data.rules}
                            actualEntries={data.actual_entries}
                            readOnly={periodClosed || !canManageAbsences(user)}
                            absencesByEmployee={absencesByEmployee}
                            onAbsenceOpen={absenceUi.onAbsenceOpen}
                        />
                    </>
                )}
            </div>
            {absenceUi.dialogs}

            {editorOpen && editorContext && (
                <ActualCellEditor
                    open={editorOpen}
                    onOpenChange={setEditorOpen}
                    employeeName={editorContext.employee_name}
                    workDate={editorContext.work_date}
                    existingBlocks={editorContext.blocks}
                    reviewed={editorContext.reviewed}
                    reviewable={editorContext.reviewable}
                    rules={editorContext.rules}
                    clockEvidence={editorContext.clock_evidence}
                    onCreate={doCreate}
                    onUpdate={doUpdate}
                    onDelete={doDelete}
                    onMarkReviewed={() => setReviewed(true)}
                    onMarkNotReviewed={() => setReviewed(false)}
                    staffMeal={editorContext.staff_meal}
                    onStaffMealChange={saveStaffMeal}
                    readOnly={Boolean(periodClosed)}
                    pending={pending}
                    canCorrectReviewed={["restaurant", "restaurant_admin", "corporate"].includes(user?.role)}
                />
            )}
        </div>
    );
}
